import { useMemo } from 'react';

import type { PetitionMarqueeCardData } from '../usePetitionMarquee';

// With the fixed-duration CSS loop, a row's width sets its apparent speed - so both rows always
// get exactly the same number of cards, and the cap bounds DOM size and speed no matter how many
// petitions are curated (the FCM can hold ~100; the rest rotate in via the per-load shuffle).
// The minimum keeps a half-track wider than the hero container so the loop never shows a gap.
export const MAX_CARDS_PER_ROW = 12;
export const MIN_CARDS_PER_ROW = 6;

export type MarqueeRows = {
	/** The always-visible line (the only one on desktop). */
	main: readonly PetitionMarqueeCardData[];
	/** The mobile-only top line. */
	secondary: readonly PetitionMarqueeCardData[];
};

// Cycle the pool until the row is full, so short curations still fill a row.
// MarqueeTrack renders the repeats as duplicates (aria-hidden, untabbable, still clickable).
function fillRow(pool: readonly PetitionMarqueeCardData[], size: number): readonly PetitionMarqueeCardData[] {
	return Array.from({ length: size }, (_, index) => pool[index % pool.length]);
}

export function selectMarqueeRows(petitions: readonly PetitionMarqueeCardData[]): MarqueeRows {
	if (petitions.length === 0) return { main: [], secondary: [] };

	const rowSize = Math.max(MIN_CARDS_PER_ROW, Math.min(MAX_CARDS_PER_ROW, Math.floor(petitions.length / 2)));
	const mainPool = petitions.slice(0, rowSize);
	// Prefer petitions the main row isn't showing; a curation smaller than one row can't avoid
	// repeating across rows, so it falls back to the whole list - rotated by half so the two
	// lines at least don't read as mirrored copies.
	const secondaryPool =
		petitions.length > rowSize
			? petitions.slice(rowSize, rowSize * 2)
			: [...petitions.slice(Math.ceil(petitions.length / 2)), ...petitions.slice(0, Math.ceil(petitions.length / 2))];

	return {
		main: fillRow(mainPool, rowSize),
		secondary: fillRow(secondaryPool, rowSize),
	};
}

/**
 * Picks which petitions each marquee line shows: a same-size sample per line, disjoint between
 * the lines whenever the curation is big enough. The per-load randomness comes from the
 * middle-end, which shuffles the curated list per request - shuffling on the client instead
 * would visibly swap the SSR-painted cards after hydration.
 */
export function useMarqueeRows(petitions: readonly PetitionMarqueeCardData[]): MarqueeRows {
	return useMemo(() => selectMarqueeRows(petitions), [petitions]);
}
